import React, { Suspense, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { Article, ContentWrap, MainLayout, TextBox } from "styles/GlobalStyles";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useLanguageChange } from "constant/locales/useLanguageChange";
import { EventSourcePolyfill } from "event-source-polyfill";
import { isEqual } from "lodash";
import { usePopup, WindowProvider } from "core/context/useWindow";
import useRoutes from "./useAllRoutes";
import { useCommonState } from "core/context/useContext";
import { useDispatch, useSelector } from "react-redux";
import { setUserLogin } from "@/core/store/user/actions";
import Inspection from "@/components/inspection/Inspection";
import api from "core/services/api";
import styled from "styled-components";
import logoImg from "assets/image/logo_gold.png";

const CenterLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 50vh;
`;

const LogoImage = styled.img`
  max-width: 200px;
  height: auto;
`;

const Routers = () => {
  useLanguageChange();
  const { initSiteName, setSiteOpenType } = useCommonState();

  const [inspection, setInspection] = useState(null);

  useEffect(() => {
    const sseUrl = `${process.env.REACT_APP_BASE_URL}/v1/site`;
    const sse = new EventSourcePolyfill(sseUrl);

    try {
      sse.addEventListener("update", (e) => {
        if (e?.data) {
          const newData = JSON.parse(e.data);
          // site 테이블의 currency_type → siteCurrency (잔액 통화 매핑에 사용)
          // update 이벤트가 반복 push되므로, 값이 실제로 바뀔 때만 디스크에 쓴다
          if (newData.currency_type && localStorage.getItem("siteCurrency") !== newData.currency_type) {
            localStorage.setItem("siteCurrency", newData.currency_type);
          }
          // 유저 화면 표시 소수점(site.user_decimal_places, 쿼리서 decimal_places 로 alias)
          if (newData.decimal_places != null && localStorage.getItem("userDecimalPlaces") !== String(newData.decimal_places)) {
            localStorage.setItem("userDecimalPlaces", String(newData.decimal_places));
          }
          if (!isEqual(newData, inspection)) {
            setInspection(newData);
          }

          if (!document.title) {
            initSiteName(newData.site_name);
          }

          if (newData.site_open_type) {
            const searchParams = new URLSearchParams(window.location.search);
            const urlSiteType = searchParams.get("siteType") || searchParams.get("site_open_type");
            const designType = searchParams.get("designType");

            if (!urlSiteType && !designType) {
              setSiteOpenType(String(newData.site_open_type));
            }
          }
        }
      });

      sse.addEventListener("ping", () => {});

      sse.onerror = () => {
        sse.close();
      };

      return () => {
        sse.close();
      };
    } catch (error) {
      console.error("Error initializing SSE:", error);
      sse.close();
    }
  }, []);

  if (!inspection) return;

  const isInspectionActive = (ins) => {
    return (
      ins.allow_yn === "N" ||
      ins.inspec_yn === "Y" ||
      (ins.plan_inspec_yn === "Y" && ins.plan_inspec_current_time_in_range)
    );
  };

  return isInspectionActive(inspection) ? (
    <Inspection inspection={inspection} />
  ) : (
    <Router>
      <WindowProvider>
        <RouteContent />
      </WindowProvider>
    </Router>
  );
};

const RouteContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [urlParams] = useSearchParams();
  const dispatch = useDispatch();

  const { openPopup, closePopup } = usePopup();
  const { isLogin, getUser, siteOpenType, setSiteOpenType } = useCommonState();
  const infoData = useSelector((state) => state.Data);

  const [checkState, setCheckState] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const key = params.get("key");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSiteType = searchParams.get("siteType") || searchParams.get("site_open_type");
    const designType = searchParams.get("designType")?.toLowerCase();

    const designTypeToSiteType = {
      open: "1",
      "semi-close": "2",
      "semi-closed": "2",
      close: "3",
      closed: "3",
    };

    const finalSiteType = urlSiteType || (designType ? designTypeToSiteType[designType] : null);

    if (finalSiteType && finalSiteType !== siteOpenType) {
      setSiteOpenType(finalSiteType);
    }
  }, [location.search]);

  // 게임 목록 화면 안에서 게임사만 바꾼 경우(/m/game-list/A → B)는 보던 위치를 그대로 둔다 — 목록만 바뀌게
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    if (prev !== pathname && prev.startsWith("/m/game-list/") && pathname.startsWith("/m/game-list/")) return;
    window.scrollTo(0, 0);
  }, [pathname]);

  const isMobile = useMediaQuery({ maxWidth: 850 });

  const isMobilePath = pathname === "/m" || pathname.startsWith("/m/");
  // 베팅내역(/bet-history)은 모바일/데스크톱 공용 경로 (리다이렉트 제외)
  const isBetHistory = pathname === "/bet-history";

  useEffect(() => {
    // 모바일 기기에서 /bet-history 접속 시에도 모바일 스타일 적용
    document.body.classList.toggle("is-mobile", isMobilePath || (isMobile && isBetHistory));
    return () => {
      document.body.classList.remove("is-mobile");
    };
  }, [isMobilePath, isMobile, isBetHistory]);

  useEffect(() => {
    // /bet-history 는 모바일에서도 그대로 노출 (아래 모바일 리다이렉트 스킵)
    if (pathname === "/" || pathname === "/oauth/redirect" || isBetHistory) return;

    let shouldNavigate = false;

    if (isMobile && !isMobilePath) {
      shouldNavigate = true;
      if (pathname === "/main") navigate("/m", { replace: true });
      else if (pathname.startsWith("/game-play/")) navigate(`/m${pathname}`, { replace: true });
      else if (pathname.startsWith("/lobby/")) navigate(pathname.replace("/lobby/", "/m/game-list/"), { replace: true });
      else navigate("/m", { replace: true });
    } else if (!isMobile && isMobilePath) {
      shouldNavigate = true;
      if (pathname === "/m") navigate("/main", { replace: true });
      else if (pathname.startsWith("/m/game-play/")) navigate(pathname.replace("/m/game-play/", "/game-play/"), { replace: true });
      else if (pathname.startsWith("/m/game-list/")) navigate(pathname.replace("/m/game-list/", "/lobby/"), { replace: true });
      else navigate("/main", { replace: true });
    }

    if (shouldNavigate) closePopup();
  }, [isMobile, pathname, isMobilePath]);

  useEffect(() => {
    if (pathname === "/oauth/redirect") {
      const error = urlParams.get("error");
      const error2 = urlParams.get("error2");
      if (error) alert(t(`error.${error}`));
      if (error2) alert(t(`error2.${error2}`));
    } else {
      if (infoData.state === "W" && pathname !== "/") {
        navigate("/");
      }

      const isSiteType3 = siteOpenType === "3";
      if (isSiteType3 && !isLogin && pathname !== "/") {
        navigate("/");
        return;
      }
    }
    setCheckState(true);
  }, [infoData.state, pathname]);

  useEffect(() => {
    if (isLogin) {
      getUser();
    }
  }, [isLogin]);

  const isSiteType3 = siteOpenType === "3";
  const shouldHideContent = isSiteType3 && !isLogin;

  useEffect(() => {
    if (key) {
      api
        .post("/auth/verify-access", { key })
        .then(async (res) => {
          dispatch(setUserLogin(res.data.content));
          const targetScope = window.location.origin;
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            if (registration.scope === `${targetScope}/`) {
              await registration.unregister();
            }
          }
          window.location.replace("/");
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            openPopup({
              title: t("alert.fail_login"),
              content: t("alert.fail_login_content1"),
            });
          }
        });
    }
  }, []);

  return (
    <>
      <MainLayout $grandMain>
        <ContentWrap>
          {shouldHideContent && (
            <CenterLogo>
              <LogoImage src={logoImg} alt="Logo" />
            </CenterLogo>
          )}
          {checkState && !shouldHideContent && <RouteContentSub />}
        </ContentWrap>
      </MainLayout>
    </>
  );
};

const RouteContentSub = () => {
  const { routeCollection } = useRoutes();

  return (
    <Suspense fallback={null}>
      <Article $grandMain>
        <Routes>
          {routeCollection.map((route, index) => (
            <Route key={index} path={route.path} element={<route.component />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Article>
    </Suspense>
  );
};

export default Routers;
